import { useState, useEffect, useCallback, useRef } from "react";

interface configType {
  persist: boolean;
}

// Define the return type for the hook
type UseSessionStorageReturn<T> = [
  value: T,
  setValue: React.Dispatch<React.SetStateAction<T>>,
  removeValue: () => void
];

function useSessionStorage<T>(
  key: string,
  defaultValue: T,
  config: configType = { persist: true }
): UseSessionStorageReturn<T> {
  // This ref will help us avoid the infinite loop
  const initializedRef = useRef(false);

  const getValue = useCallback((): T => {
    try {
      const storedValue = sessionStorage.getItem(key);
      if (storedValue === null) {
        return defaultValue;
      }

      try {
        // First try to parse as JSON
        return JSON.parse(storedValue);
      } catch (e) {
        // If parsing fails, try to return the raw value as type T
        // This works for primitive types like string, number, etc.
        if (typeof defaultValue === "string") {
          return storedValue as unknown as T;
        }
        if (typeof defaultValue === "number") {
          return Number(storedValue) as unknown as T;
        }
        if (typeof defaultValue === "boolean") {
          return (storedValue.toLowerCase() === "true") as unknown as T;
        }

        // If we can't convert to type T, fallback to default value
        console.warn(`Unable to parse stored value for '${key}' to expected type`);
        return defaultValue;
      }
    } catch (e) {
      // Any other errors (like InvalidCharacterError), return default
      console.error(`Error retrieving '${key}' from sessionStorage:`, e);
      return defaultValue;
    }
  }, [key, defaultValue]);

  const [storedValue, setStoredValue] = useState<T>(getValue);

  const loadStorage = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        if (value instanceof Function) {
          setStoredValue((prevValue) => {
            const valueToStore = value(prevValue);
            if (config.persist) {
              window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
            }
            return valueToStore;
          });
        } else {
          setStoredValue(value);
          if (config.persist) {
            sessionStorage.setItem(key, JSON.stringify(value));
          }
        }
      } catch (error) {
        console.error("Error writing to session storage", error);
      }
    },
    [key, config.persist]
  );

  // Split the effects to avoid the dependency cycle
  useEffect(() => {
    // Only load initial value once
    if (!initializedRef.current) {
      if (sessionStorage.getItem(key) === null) {
        loadStorage(getValue());
      }
      initializedRef.current = true;
    }
  }, [getValue, key, loadStorage]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        loadStorage(getValue());
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [key, getValue, loadStorage]);

  const removeValue = useCallback(() => {
    sessionStorage.removeItem(key);
    setStoredValue(defaultValue);
  }, [key, defaultValue]);

  return [storedValue, loadStorage, removeValue];
}
export default useSessionStorage;
